/**
 * 成績表示の種別切り替え（SPEC §2.3「ポジションによる成績種別の切り替え」/ F-04）。
 *
 * 投手登録選手は投手成績、それ以外は打撃成績を主表示とする。
 * 二刀流など両方の成績を持つ選手の扱いは未決事項（SPEC §10 #7）のため、
 * ここでは position のみで単純に判定し、両成績の併記等はしない。
 *
 * 外部依存・入出力・現在時刻の取得を一切持たない純粋関数（CLAUDE.md 禁止事項6 / SPEC §7.4）。
 */

/** 打撃成績のうち、成績表示に必要な最小限のフィールド */
export interface BattingStatsLike {
  readonly year: string
  readonly team: string
  readonly games: number
  readonly battingAverage: string
  readonly homeRuns: number
  readonly runsBattedIn: number
  readonly stolenBases: number
  readonly hits: number
  readonly strikeouts: number
  readonly onBasePercentage: string
  readonly sluggingPercentage: string
}

/** 投手成績のうち、成績表示に必要な最小限のフィールド */
export interface PitchingStatsLike {
  readonly year: string
  readonly team: string
  readonly appearances: number
  readonly wins: number
  readonly losses: number
  readonly saves: number
  readonly holds: number
  readonly earnedRunAverage: string
  readonly inningsPitched: string
  readonly strikeouts: number
}

export type PrimaryStatsDisplay<Batting extends BattingStatsLike, Pitching extends PitchingStatsLike> =
  | { readonly kind: 'batting'; readonly stats: Batting }
  | { readonly kind: 'pitching'; readonly stats: Pitching }
  | { readonly kind: 'none' }

/**
 * 主表示する成績種別と、その成績データを選ぶ。
 *
 * - `position` に "投手" を含む選手は投手成績を主表示とする。投手成績が無い（null）場合は
 *   `{ kind: 'none' }`（打撃成績があってもフォールバックしない。二刀流の扱いは未決事項のため
 *   単純な規則を優先する）
 * - それ以外の選手は打撃成績を主表示とする。打撃成績が無い場合は `{ kind: 'none' }`
 * - `position` が null（プロフィール取得に失敗した等）の場合も打撃成績を主表示とする
 *
 * @param position 選手のポジション文字列（例: "投手" "内野手"）。取得できない場合は null
 * @param latestBattingStats 直近年度の打撃成績。無ければ null
 * @param latestPitchingStats 直近年度の投手成績。無ければ null
 */
export function selectPrimaryStatsDisplay<Batting extends BattingStatsLike, Pitching extends PitchingStatsLike>(
  position: string | null,
  latestBattingStats: Batting | null,
  latestPitchingStats: Pitching | null,
): PrimaryStatsDisplay<Batting, Pitching> {
  const isPitcher = position !== null && position.includes('投手')

  if (isPitcher) {
    return latestPitchingStats ? { kind: 'pitching', stats: latestPitchingStats } : { kind: 'none' }
  }
  return latestBattingStats ? { kind: 'batting', stats: latestBattingStats } : { kind: 'none' }
}
